import { axiosInstance } from "@/plugins/https";
import api from "@/_helpers/api";
import { defineStore } from "pinia";
import { useSnackbarStore } from "@/_store/snackbar.module";
import { i18n } from "@/plugins/i18n";
import { SiemConnectorFormat } from "@/constants/connectors";
import type { ServiceStatus } from "@/constants/cloud-apps";
import type { SiemErrorType } from "@/constants/syslogs.ts";
import { StoreModule } from "@/constants/store.ts";

export interface SiemConnectorProviderData {
  // Azure Sentinel — Logs Ingestion API
  tenantId?: string;
  clientId?: string;
  clientSecret?: string;
  dceEndpoint?: string;
  dcrImmutableId?: string;
  streamName?: string;
  // Azure Sentinel — legacy HTTP Data Collector (still returned for pre-migration connectors)
  /** @deprecated Legacy HTTP Data Collector field. Use the Logs Ingestion API fields above. */
  workspaceId?: string;
  /** @deprecated Legacy HTTP Data Collector field. Use the Logs Ingestion API fields above. */
  primaryKey?: string;
  /** @deprecated Legacy HTTP Data Collector field. Use the Logs Ingestion API fields above. */
  logType?: string;
  // Splunk / Generic / Fluency
  url?: string;
  headers?: { key: string; value: string }[];
}

export interface SiemConnectorBaseData {
  id: string;
  workspaceId: string;
  name: string;
  childWorkspaceIds: string[];
  format: SiemConnectorFormat;
  status: ServiceStatus;
  applyToAll: boolean;
  errorType?: SiemErrorType;
  editable: boolean;
}

export type SiemConnector = SiemConnectorBaseData &
  Partial<Record<SiemConnectorFormat, SiemConnectorProviderData>>;

/**
 * Detects pre-migration Azure Sentinel connectors, still configured via the legacy HTTP Data Collector.
 * Note: the legacy `workspaceId` lives on the provider data, `SiemConnectorBaseData.workspaceId` is the Coro
 * workspace id and is always present.
 * @param connector
 */
export const isLegacySiemConnector = (connector: SiemConnector): boolean =>
  connector.format === SiemConnectorFormat.AZURE_SENTINEL &&
  !!connector[SiemConnectorFormat.AZURE_SENTINEL]?.workspaceId;

interface SiemState {
  connectors: SiemConnector[];
  loading: boolean;
  showSkeletonLoader: boolean;
}

const defaultSiemState = {
  connectors: [],
  loading: false,
  showSkeletonLoader: false,
};

export const useSiemStore = defineStore(StoreModule.SIEM, {
  state: (): SiemState => ({ ...defaultSiemState }),
  getters: {
    hasLegacySiemConnector: (state: SiemState): boolean =>
      state.connectors.some(isLegacySiemConnector),
  },
  actions: {
    async getConnectors(showSkeletonLoader: boolean = true): Promise<void> {
      if (showSkeletonLoader) {
        this.showSkeletonLoader = true;
      }

      try {
        const response = await axiosInstance.request(api.siemConnectors());

        this.connectors = response.data.filter(
          (item: SiemConnector) => item.format !== SiemConnectorFormat.SYSLOG
        );

        this.showSkeletonLoader = false;
      } catch (err) {
        console.error(err);
        this.showSkeletonLoader = false;
        throw err;
      }
    },
    async addSiemConnector(data: SiemConnector): Promise<void> {
      try {
        this.loading = true;
        const request = {
          ...api.siemConnectors(),
          data,
          method: "post",
        };

        await axiosInstance.request(request);

        await this.getConnectors(false);

        this.loading = false;
      } catch (err) {
        this.loading = false;
        console.error(err);
        throw err;
      }
    },
    async updateConnector(data: SiemConnector): Promise<void> {
      try {
        this.loading = true;
        const request = {
          ...api.siemConnector(data.id),
          data,
          method: "put",
        };

        await axiosInstance.request(request);

        await this.getConnectors(false);

        this.loading = false;
      } catch (err) {
        this.loading = false;
        console.error(err);
        throw err;
      }
    },
    async deleteConnector(data: SiemConnector): Promise<void> {
      this.loading = true;

      try {
        const request = {
          ...api.siemConnector(data.id),
          method: "delete",
        };

        await axiosInstance.request(request);

        await this.getConnectors(false);
        this.loading = false;
      } catch (error) {
        this.loading = false;
        console.error(error);
      }
    },
    async syncConnector(data: SiemConnector): Promise<void> {
      try {
        this.loading = true;

        const request = {
          ...api.syncSiemConnector(),
          data,
          method: "post",
        };

        await axiosInstance.request(request);

        const snackbarModule = useSnackbarStore();

        snackbarModule.addGenericSuccess(
          i18n.global.t("snackbar.messages.connectors.syncCompleted")
        );

        this.loading = false;
      } catch (err) {
        this.loading = false;
        console.error(err);
      }
    },
  },
});
